import { SearchSource } from '@carvana/merch-contracts';
import { useCallback, useContext } from 'react';
import FilterEvents from '~/constants/srp/events';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import type { AppliedRangeFilters } from '~/validations/AppliedFilters';

type OwnArgs = {
  filterKey: keyof AppliedRangeFilters;
  max: number | undefined;
  min: number | undefined;
};

type ValueType = {
  min?: number;
  max?: number;
};

function compareRangeValues(
  filterKey: string,
  prev: ValueType,
  next: ValueType
): string {
  switch (true) {
    case prev.min !== next.min && prev.max !== next.max:
      return filterKey;
    case prev.min !== next.min:
      return `${filterKey}-min`;
    case prev.max !== next.max:
      return `${filterKey}-max`;
    default:
      return '';
  }
}

export default function useRangeUpdater({
  filterKey,
  max: rangeMax,
  min: rangeMin
}: OwnArgs): (range: number[] | undefined) => void {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { appliedFilters, updateRangeFilter } = useContext(
    AppliedFiltersContext
  );
  const trackEvent = useTrackEvent();

  return useCallback(
    (range: number[] = []) => {
      // ensure lowest value is always the min
      const sortedRange = [...range].sort((a, b) => a - b);
      const min = sortedRange.at(0);
      const max = sortedRange.at(1);

      const value: { max?: number; min?: number } =
        min === rangeMin && max === rangeMax ? {} : { min, max };
      if (min === rangeMin) delete value.min;
      if (max === rangeMax) delete value.max;

      updateRangeFilter({ filterKey, value });

      setSearchSource(SearchSource.Facet);
      trackEvent(FilterEvents.engagedFacet, {
        action: 'add facet',
        item: 'slider',
        label: compareRangeValues(
          filterKey,
          appliedFilters[filterKey] ?? {},
          value
        ),
        value
      });
    },
    [
      appliedFilters,
      filterKey,
      rangeMax,
      rangeMin,
      setSearchSource,
      trackEvent,
      updateRangeFilter
    ]
  );
}
